import React, { useCallback, useMemo } from 'react';

import { ColumnConfiguration, TableRow } from '../../types';
import { getColumnValue } from '../../utils';
import { BranchConfig, ColumnConfig, RootConfig, TreeTable } from '../TreeTable';
import { ContextBodyProps } from './types';
import { getColumnSortingValue } from './utils';

export interface GroupedTablesTabBodyProps extends Omit<ContextBodyProps, 'selectedTab'> {
  groupKey: string;
  searchTerm?: string;
  onSelectGroup?: (group: string) => void;
  disableSelectAllButton?: boolean;
}

const getBranchColumnConfig = (column: ColumnConfiguration): ColumnConfig => ({
  ...column,
  subComponent: (columnValue: TableRow[keyof TableRow]) => <>{getColumnValue(columnValue)}</>,
  getSortingValue: ((object: TableRow, key: keyof TableRow) => getColumnSortingValue(object, key)) as <T>(
    object: T,
    key: keyof T
  ) => string | number | Date,
});

export const GroupedTableContent: React.FC<GroupedTablesTabBodyProps> = ({
  tableInfo,
  selectedRows,
  onRowSelection,
  rowSelectionMode,
  groupKey,
  searchTerm,
  onSelectGroup,
  disableSelectAllButton,
}) => {
  if (!groupKey) {
    throw Error('groupKey is required for GroupedTables Tab');
  }

  const formatBranchColumn = useCallback(getBranchColumnConfig, []);

  // Extract groupBy column and non-grouped columns
  const { groupByColumnConfig, nonGropedColumnConfigs } = useMemo(() => {
    let groupByColumnConfig: ColumnConfiguration;
    const nonGropedColumnConfigs: ColumnConfig[] = [];

    (tableInfo.columnConfig ?? []).forEach(column => {
      if (column.columnKey === groupKey) {
        groupByColumnConfig = column;
      } else {
        nonGropedColumnConfigs.push(formatBranchColumn(column));
      }
    });

    return { groupByColumnConfig, nonGropedColumnConfigs };
  }, [tableInfo.columnConfig, groupKey, formatBranchColumn]);

  if (!groupByColumnConfig) {
    throw Error(`Column with key ${groupKey} not found in tableInfo.columnConfig`);
  }

  const rootConfigOptions: RootConfig<TableRow> = useMemo(
    () => ({
      header: groupByColumnConfig.columnName,
      groupBy: groupKey,
      isSortingEnabled: groupByColumnConfig.isSortingEnabled,
      getDisplayValue: (columnValue: TableRow[keyof TableRow]) => getColumnValue(columnValue),
      getSortingValue: (rootObject: TableRow, rootKey: keyof TableRow) => getColumnSortingValue(rootObject, rootKey),
      onSelectGroup,
    }),
    [groupByColumnConfig, groupKey, onSelectGroup]
  );
  const branchConfigOptions: BranchConfig = useMemo(
    () => ({
      columns: nonGropedColumnConfigs,
      rowSelectionMode,
      selectedRows,
      onRowSelectionChange: onRowSelection,
      disableSelectAllButton,
    }),
    [nonGropedColumnConfigs, rowSelectionMode, selectedRows, onRowSelection, disableSelectAllButton]
  );

  return (
    <TreeTable<TableRow>
      rawData={tableInfo.data}
      rootConfigOptions={rootConfigOptions}
      branchConfigOptions={branchConfigOptions}
      searchTerm={searchTerm}
    />
  );
};
